import { computed, shallowRef } from 'vue';
import { apiRequest } from '@/api/http';
import { NON_GRANTABLE_ROLE_CODES, RoleCode } from '@/constants/accounts';

// Every role (assigned module), loaded once and shared. Screens that deal
// with other users' roles use it to map between role ids and codes.
const roles = shallowRef([]);
let pending = null;

// Resolves once the roles are loaded (requests them the first time).
function loadRoles() {
  pending ??= apiRequest('get', '/assigned-modules')
    .then((rows) => {
      roles.value = Array.isArray(rows) ? rows : [];
    })
    .catch(() => {
      pending = null; // retry next time a screen asks
    });
  return pending;
}

export function useModuleOptions() {
  loadRoles();

  // Roles an admin can grant to a faculty account.
  const grantableRoles = computed(() =>
    roles.value.filter((role) => !NON_GRANTABLE_ROLE_CODES.includes(role.code)),
  );

  const findRoleId = (code) => roles.value.find((role) => role.code === code)?.id ?? null;
  const findRoleCode = (id) => roles.value.find((role) => Number(role.id) === Number(id))?.code ?? null;

  // Adviser and Subject Teacher are "2 in 1": an adviser also teaches. While
  // Adviser is picked, Subject Teacher is left out of the list (and of the
  // picked chips), and it's added back when saving.
  const designationOptions = (picked) => {
    const adviser = findRoleId(RoleCode.Adviser);
    const teacher = findRoleId(RoleCode.Teacher);
    const hasAdviser = (picked || []).some((id) => Number(id) === Number(adviser));
    return hasAdviser ? grantableRoles.value.filter((role) => Number(role.id) !== Number(teacher)) : grantableRoles.value;
  };
  const withoutImpliedTeacher = (picked) => {
    const adviser = findRoleId(RoleCode.Adviser);
    const teacher = findRoleId(RoleCode.Teacher);
    const list = (picked || []).map(Number);
    return list.includes(Number(adviser)) ? list.filter((id) => id !== Number(teacher)) : list;
  };
  const withImpliedTeacher = (picked) => {
    const adviser = findRoleId(RoleCode.Adviser);
    const teacher = findRoleId(RoleCode.Teacher);
    const list = (picked || []).map(Number);
    return list.includes(Number(adviser)) && teacher && !list.includes(Number(teacher)) ? [...list, Number(teacher)] : list;
  };

  return {
    roles,
    grantableRoles,
    findRoleId,
    findRoleCode,
    loadRoles,
    designationOptions,
    withoutImpliedTeacher,
    withImpliedTeacher,
  };
}
